import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useAuth } from '@/hooks/useAuth';
import {
  fetchAuditLogs, fetchAuditFacets, fetchAuditLog, fetchAuthEvents,
} from '@/api/endpoints';
import type { AuditLogQuery, AuthEventQuery } from '@/api/types';

const PAGE_SIZE = 50;

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
}

function statusBadge(s: string): string {
  return s === 'success' ? 'badge-green' : 'badge-slate';
}

type Tab = 'logs' | 'events';

export default function Audit({ device }: { device: Device }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superAdmin';
  const [tab, setTab] = useState<Tab>('logs');

  return (
    <PageShell device={device} title="Log des Mises À Jour" breadcrumb="Système">
      <div className="p-4 md:p-6 space-y-4">
        {isSuperAdmin && (
          <div className="flex h-9 w-fit rounded-md border border-input bg-background overflow-hidden" role="tablist">
            <button onClick={() => setTab('logs')} className={`px-4 text-sm font-medium ${tab === 'logs' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Mises à jour</button>
            <button onClick={() => setTab('events')} className={`px-4 text-sm font-medium border-l border-border ${tab === 'events' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Authentification 🔒</button>
          </div>
        )}
        {tab === 'logs' ? <AuditLogsView /> : <AuthEventsView />}
      </div>
    </PageShell>
  );
}

// ─── audit_logs view ────────────────────────────────────────────────────────
function AuditLogsView() {
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [status, setStatus] = useState<'' | 'success' | 'failure'>('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const reset = () => setPage(0);
  const query: AuditLogQuery = {
    action: action || undefined,
    target_type: targetType || undefined,
    status: status || undefined,
    q: search.trim() || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const { data, isLoading, isError, error } = useQuery({ queryKey: ['audit', 'logs', query], queryFn: () => fetchAuditLogs(query) });
  const { data: facets } = useQuery({ queryKey: ['audit', 'facets'], queryFn: fetchAuditFacets });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={action} onChange={(e) => { setAction(e.target.value); reset(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm max-w-[200px]">
          <option value="">action — toutes</option>
          {(facets?.actions ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={targetType} onChange={(e) => { setTargetType(e.target.value); reset(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm">
          <option value="">target — tous</option>
          {(facets?.target_types ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value as '' | 'success' | 'failure'); reset(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm">
          <option value="">statut — tous</option>
          <option value="success">success</option>
          <option value="failure">failure</option>
        </select>
        <input type="search" placeholder="Rechercher action / target / acteur…" value={search} onChange={(e) => { setSearch(e.target.value); reset(); }} className="h-9 px-3 rounded-md border border-input bg-background text-sm flex-1 min-w-[180px] max-w-xs" />
        <label className="text-xs text-muted-foreground flex items-center gap-1">du <input type="datetime-local" value={from} onChange={(e) => { setFrom(e.target.value); reset(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm" /></label>
        <label className="text-xs text-muted-foreground flex items-center gap-1">au <input type="datetime-local" value={to} onChange={(e) => { setTo(e.target.value); reset(); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm" /></label>
        <span className="text-xs text-muted-foreground ml-auto">{isLoading ? 'Chargement…' : `${total} entrée(s)`}</span>
      </div>

      {isError ? (
        <div className="p-8 text-center">
          <p className="text-destructive font-medium">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5 w-16">id</th>
                <th className="text-left px-4 py-2.5">date</th>
                <th className="text-left px-4 py-2.5">acteur</th>
                <th className="text-left px-4 py-2.5">action</th>
                <th className="text-left px-4 py-2.5">cible</th>
                <th className="text-left px-4 py-2.5">statut</th>
                <th className="text-left px-4 py-2.5 w-16">état</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-accent/30 select-none" onClick={() => setSelectedId(r.id)} title="Cliquer pour détail">
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.id}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtDate(r.occurred_at)}</td>
                  <td className="px-4 py-2.5 text-xs">{r.actor_email ?? '—'}{r.actor_role && <span className="ml-1 text-muted-foreground">({r.actor_role})</span>}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.action}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.target_type}{r.target_id ? `:${r.target_id}` : ''}</td>
                  <td className="px-4 py-2.5"><span className={`${statusBadge(r.status)} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{r.status}</span></td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground" title="états avant/après enregistrés">
                    {r.has_before_state || r.has_after_state ? <span className="font-mono">{r.has_before_state ? '◀' : '·'}{r.has_after_state ? '▶' : '·'}</span> : '—'}
                  </td>
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">Aucune entrée dans le filtre.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">←</button>
          <span className="text-xs text-muted-foreground">page {page + 1} / {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">→</button>
        </div>
      )}

      {selectedId != null && <AuditLogDetailSheet id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function AuditLogDetailSheet({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: d, isLoading } = useQuery({ queryKey: ['audit', 'log', id], queryFn: () => fetchAuditLog(id) });

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full md:w-[760px] bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-start justify-between z-10">
          <div>
            <div className="text-xs text-muted-foreground">Entrée #{id}</div>
            <h2 className="text-lg font-bold font-mono mt-0.5">{d?.action ?? '…'}</h2>
            {d && <div className="text-xs text-muted-foreground font-mono mt-0.5">{fmtDate(d.occurred_at)} · {d.target_type}{d.target_id ? `:${d.target_id}` : ''}</div>}
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" aria-label="Fermer">✕</button>
        </div>

        <div className="p-5 space-y-4 flex-1">
          {isLoading || !d ? (
            <div className="text-sm text-muted-foreground">Chargement…</div>
          ) : (
            <>
              <dl className="text-sm grid grid-cols-[130px_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-muted-foreground text-xs uppercase">acteur</dt>
                <dd className="font-mono text-xs">{d.actor_email ?? '—'} {d.actor_role && `(${d.actor_role})`}</dd>
                <dt className="text-muted-foreground text-xs uppercase">statut</dt>
                <dd><span className={`${statusBadge(d.status)} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{d.status}</span></dd>
                {d.error_message && (<><dt className="text-muted-foreground text-xs uppercase">erreur</dt><dd className="text-xs text-destructive break-all">{d.error_message}</dd></>)}
                <dt className="text-muted-foreground text-xs uppercase">ip</dt>
                <dd className="font-mono text-xs">{d.ip_address ?? '—'}</dd>
                <dt className="text-muted-foreground text-xs uppercase">user-agent</dt>
                <dd className="font-mono text-xs break-all">{d.user_agent ?? '—'}</dd>
              </dl>

              {(d.before_state != null || d.after_state != null) && (
                <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Avant</h3>
                    <pre className="bg-muted/30 font-mono text-[11px] p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all border border-border min-h-[2rem]">{d.before_state != null ? JSON.stringify(d.before_state, null, 2) : '—'}</pre>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Après</h3>
                    <pre className="bg-muted/30 font-mono text-[11px] p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all border border-border min-h-[2rem]">{d.after_state != null ? JSON.stringify(d.after_state, null, 2) : '—'}</pre>
                  </div>
                </section>
              )}

              {d.metadata != null && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Metadata</h3>
                  <pre className="bg-muted/30 font-mono text-[11px] p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all border border-border">{JSON.stringify(d.metadata, null, 2)}</pre>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

// ─── auth_events view (superAdmin) ──────────────────────────────────────────
function AuthEventsView() {
  const [email, setEmail] = useState('');
  const [eventType, setEventType] = useState('');
  const [page, setPage] = useState(0);

  const query: AuthEventQuery = {
    email: email.trim() || undefined,
    event_type: eventType || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['audit', 'events', query], queryFn: () => fetchAuthEvents(query) });
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const EVENT_TYPES = ['login_success', 'login_failure', 'logout', 'password_change', 'lockout'];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <input type="search" placeholder="email…" value={email} onChange={(e) => { setEmail(e.target.value); setPage(0); }} className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[180px] max-w-xs" />
        <select value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(0); }} className="h-9 px-2 rounded-md border border-input bg-background text-sm">
          <option value="">type — tous</option>
          {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{isLoading ? 'Chargement…' : `${total} événement(s)`}</span>
      </div>

      {isError ? (
        <div className="p-8 text-center">
          <p className="text-destructive font-medium">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5 w-16">id</th>
                <th className="text-left px-4 py-2.5">date</th>
                <th className="text-left px-4 py-2.5">email</th>
                <th className="text-left px-4 py-2.5">événement</th>
                <th className="text-left px-4 py-2.5">ip</th>
                <th className="text-left px-4 py-2.5">user-agent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.id}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtDate(r.occurred_at)}</td>
                  <td className="px-4 py-2.5 text-xs">{r.email ?? '—'}</td>
                  <td className="px-4 py-2.5"><span className={`${r.event_type.includes('failure') || r.event_type === 'lockout' ? 'badge-slate' : 'badge-blue'} px-1.5 py-0.5 rounded text-[10px] font-medium font-mono`}>{r.event_type}</span></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.ip_address ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-xs">{r.user_agent ?? '—'}</td>
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">Aucun événement.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">←</button>
          <span className="text-xs text-muted-foreground">page {page + 1} / {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="h-8 px-3 rounded-md border border-border disabled:opacity-40">→</button>
        </div>
      )}
    </div>
  );
}
